import DynamicForm from '@remote-com/mushu/src/components/Form/DynamicForm';
import {
  SuccessTitle,
  SuccessDescription,
  SuccessActions,
} from '@remote-com/mushu/src/components/Success';
import SuccessMark from '@remote-com/mushu/src/components/Success/SuccessMark';
import { Checkbox } from '@remote-com/mushu/src/components/Ui/Form/Checkbox';
import { GlobalStyles, theme as mushuTheme } from '@remote-com/mushu/src/config/styles';
import { useFormEvents } from '@remote-com/mushu/src/hooks/useFormEvents';
import { ButtonInline } from '@remote-com/norma/core/button';
import { darken } from 'polished';
import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button } from 'remote-com-shared/components/Button';
import { dataLayerPush } from 'remote-com-shared/components/CookieScripts/helpers';
import {
  createPartnerStackSignup,
  getCurrentURL,
  registerLead,
} from 'remote-com-shared/components/ModalForm/helpers/services';
import {
  SubsribeSuccessContainer,
  FormError,
  DynamicFormWrapper,
} from 'remote-com-shared/components/ModalForm/styles';
import { getFields } from 'remote-com-shared/domains/hubspot/fields';
import { handleFormRedirect } from 'remote-com-shared/domains/hubspot/helpers';
import { hasImpactFields } from 'remote-com-shared/domains/impact/helpers';
import { LocaleContextConsumer } from 'remote-com-shared/domains/locales/context';
import { useImpactFormValues } from 'remote-com-shared/domains/shared/trackingParameters';
import { getPartnerStackInfo } from 'remote-com-shared/utils/partnerstack';
import styled, { ThemeProvider } from 'styled-components';
import { v4 as uuid4 } from 'uuid';

import {
  FormActions,
  CaptchaWrapper,
  lengthRequirementMessage,
  nameMaxLength,
  nameLengthMessage,
} from '@/src/views/Connect2022/ApplyForm';
import { ContentSectionButton } from '@/src/views/Connect2022/ContentSection';

const SuccessContainer = styled(SubsribeSuccessContainer)`
  margin-top: 40px;

  a {
    font-size: inherit;
  }
`;

const SubmitButton = styled(ContentSectionButton)`
  background-color: var(--colors-irisBlue);

  &:hover {
    background-color: ${darken(0.1, '#624DE3')};
  }
`;

const SubmitContainer = styled(SubmitButton)`
  background-color: #5700ff;
  box-shadow: 0px 6px 12px rgba(98, 77, 227, 0.3);
  border-radius: 50px;
`;

const ApplyFormWrapper = styled.div`
  --dynamicFormContainerMargin: 0;
  --checkboxStateSize: 16px;

  ${Checkbox} {
    padding: 20px 0;
  }
`;
export const SuccessMessage = styled(SuccessDescription)`
  a {
    background: var(--colors-irisBlue);
    color: var(--colors-blank);
    padding: 15px 30px;
    border-radius: 25px;
    border: none;

    &:hover,
    &:focus {
      border: 1px solid var(--colors-turquoise);
    }
  }
`;

export function Form({
  hubspotFormDefinition,
  onSuccessChange,
  gatedFile,
  gatedFileDownloadLabel,
  modalFormSubmitLabel,
}) {
  const {
    submitText,
    inlineMessage: successMessage,
    formFieldGroups,
    redirect,
  } = hubspotFormDefinition;
  const hasRedirect = (hubspotFormDefinition?.redirect || '').length > 0;

  const captchaEnabled = true;

  const [showLoader, setShowLoader] = useState(false);
  const [showFormError, setShowFormError] = useState(false);
  const [isCaptchaDone, setIsCaptchaDone] = useState(!captchaEnabled);
  const [showSuccessState, setShowSuccessState] = useState(false);
  const reCaptchaRef = useRef(null);
  const impactFormValues = useImpactFormValues();

  const formName = `form-${hubspotFormDefinition.guid}`;

  const fields = getFields(formFieldGroups).map((field) => {
    switch (field.name) {
      case 'firstname': {
        field.schema = field.schema.max(nameMaxLength, nameLengthMessage);
        break;
      }
      case 'lastname': {
        field.schema = field.schema.when('firstname', (firstnameValue, schema) =>
          schema.max(nameMaxLength - (firstnameValue || '').length, nameLengthMessage)
        );
        break;
      }

      case 'email': {
        field.schema = field.schema.max(50, lengthRequirementMessage);
        break;
      }

      case 'company':
      case 'jobtitle':
      case 'city':
      case 'country': {
        field.schema = field.schema.max(30, lengthRequirementMessage);
        break;
      }
      default:
        break;
    }

    // Exception to remove job function
    const { name, label } = field;
    if (name.includes('job_function') && label.includes(' - ')) {
      const { placeholder } = field;
      const isPlaceholderLikeLabel = label === placeholder;
      const [, normalizedLabel] = label.split(' - ').map((entry) => entry.trim(entry));

      field.label = normalizedLabel;

      if (isPlaceholderLikeLabel) {
        field.placeholder = normalizedLabel;
      }
    }

    return field;
  });

  const { formState } = useFormEvents();
  const [formValues, setFormValues] = useState({});

  function handleCaptchaChange(value) {
    setIsCaptchaDone(value);
  }

  async function handleSubmit(values, { setSubmitting }) {
    setShowLoader(true);

    const updatedFormValues = {
      ...formValues,
      ...values,
    };
    setFormValues(updatedFormValues);

    if (isCaptchaDone) {
      const includePartnerstack =
        Object.prototype.hasOwnProperty.call(values, 'pspartnerkey') &&
        Object.prototype.hasOwnProperty.call(values, 'pspartner_group');
      const includeImpactValues = hasImpactFields(values);

      try {
        if (includePartnerstack) {
          try {
            const publicSlug = uuid4();
            await createPartnerStackSignup({
              name: values.name,
              email: publicSlug,
            });
          } catch {
            // Ignore as failing should not break hubspot
          }
        }

        const payload = {
          ...values,
          ...(Object.prototype.hasOwnProperty.call(values, 'utm')
            ? {
                utm: getCurrentURL(),
              }
            : {}),
          ...(includePartnerstack ? getPartnerStackInfo() : {}),
          ...(includeImpactValues ? impactFormValues : {}),
        };

        await registerLead(
          payload,
          `https://api.hsforms.com/submissions/v3/integration/submit/${hubspotFormDefinition.portalId}/${hubspotFormDefinition.guid}/`
        );

        dataLayerPush({
          event: 'user-submitted-hubspot-form',
          label: window.location.pathname,
          conversion: { send_to: 'AW-594140685/SLovCLa8nOEBEI28p5sC' },
        });

        setShowLoader(false);
        setShowSuccessState(true);
        if (typeof onSuccessChange === 'function') {
          onSuccessChange(true);
        }

        if (hasRedirect) {
          handleFormRedirect(redirect);
        }
      } catch (exception) {
        if (captchaEnabled) {
          reCaptchaRef.current.reset();
          setIsCaptchaDone(false);
        }
        setShowFormError(true);
        setShowLoader(false);
        setSubmitting(false);

        throw exception;
      }
    }
  }

  return (
    <ApplyFormWrapper>
      <ThemeProvider theme={{ mode: 'light', ...mushuTheme }}>
        <GlobalStyles />

        {showSuccessState ? (
          <SuccessContainer>
            <SuccessMark />
            <SuccessTitle>Thank you!</SuccessTitle>
            <SuccessMessage dangerouslySetInnerHTML={{ __html: successMessage }} />
            {hasRedirect && <SuccessMessage>Redirecting…</SuccessMessage>}
            {gatedFile && (
              <SuccessActions>
                <Button href={gatedFile.url} target={gatedFile.target}>
                  {gatedFileDownloadLabel || 'Download'}
                </Button>
              </SuccessActions>
            )}
          </SuccessContainer>
        ) : (
          <DynamicFormWrapper>
            {showFormError && (
              <FormError>
                Something went wrong. Please try again, or contact{' '}
                <ButtonInline href="mailto:help@remote.com">help@remote.com</ButtonInline> if this
                error persists. Thank you.
              </FormError>
            )}

            <DynamicForm
              formName={formName}
              fields={fields}
              fieldValues={formValues}
              formError={formState.errorMessage}
              formTheme="dark"
              formSize="small"
              handleOnSubmit={handleSubmit}
              isLoading={formState.isLoading}
              enableReinitialize
            />

            {captchaEnabled && (
              <CaptchaWrapper>
                <LocaleContextConsumer>
                  {(locale) => (
                    <ReCAPTCHA
                      hl={locale}
                      ref={reCaptchaRef}
                      sitekey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}
                      onChange={handleCaptchaChange}
                    />
                  )}
                </LocaleContextConsumer>
              </CaptchaWrapper>
            )}

            <FormActions>
              <SubmitContainer
                as="button"
                type="submit"
                isLoading={showLoader}
                disabled={!isCaptchaDone}
                form={formName}
              >
                {modalFormSubmitLabel || submitText || 'Submit'}
              </SubmitContainer>
            </FormActions>
          </DynamicFormWrapper>
        )}
      </ThemeProvider>
    </ApplyFormWrapper>
  );
}
